import React, { useCallback, useState } from 'react';
import { ReceiveStockModal } from '@components/organisms/ReceiveStockModal';
import { WarehouseAlertsSlideOver } from '@components/organisms/WarehouseAlertsSlideOver';
import { useLowStockItems, useLeadTimeAlertCounts } from '@hooks/api/warehouse';
import { Warehouse } from 'lucide-react';

/**
 * Dashboard "Warehouse N" alerts pill. Lives in the activity triage row and is
 * styled to match the other triage pills (yellow/warning theme). Mirrors the
 * sidebar/warehouse Alerts count (low stock + lead-time) and, on click, opens a
 * slide-over with the low-stock alert cards. Receiving stock opens
 * ReceiveStockModal; while it's open the slide-over is hidden so the modal isn't
 * trapped behind its higher z-index layer, then it reappears (with refreshed
 * data) on close.
 */
export const WarehouseAlertsButton: React.FC = () => {
  const [isSlideOpen, setIsSlideOpen] = useState(false);
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<
    string | undefined
  >();

  // Same count as the left-sidebar Warehouse badge: low stock + lead-time alerts.
  const { data: lowStockItems = [] } = useLowStockItems();
  const { data: leadTimeAlertCounts } = useLeadTimeAlertCounts();
  const warehouseAlertsCount =
    lowStockItems.length + (leadTimeAlertCounts?.total || 0);

  const handleOrder = useCallback((catalogId: string) => {
    setSelectedCatalogId(catalogId);
    setIsReceiveOpen(true);
  }, []);

  const handleReceiveClose = useCallback(() => {
    setIsReceiveOpen(false);
    setSelectedCatalogId(undefined);
  }, []);

  if (warehouseAlertsCount === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsSlideOpen(true)}
        title="View warehouse alerts"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap border bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100">
        <Warehouse className="w-3.5 h-3.5" />
        Warehouse
        <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-yellow-100 text-yellow-700">
          {warehouseAlertsCount}
        </span>
      </button>

      {/* Hidden while the receive modal is open so the modal (lower z-index)
          isn't covered; reappears on close with refreshed query data. */}
      <WarehouseAlertsSlideOver
        isOpen={isSlideOpen && !isReceiveOpen}
        onClose={() => setIsSlideOpen(false)}
        onOrder={handleOrder}
      />

      <ReceiveStockModal
        isOpen={isReceiveOpen}
        onClose={handleReceiveClose}
        initialCatalogId={selectedCatalogId}
      />
    </>
  );
};
